import {Button, Heading, Input, Stat, StatGroup, StatLabel, StatNumber, Tag,} from '@chakra-ui/react';
import React from 'react';
import {Cell, Row} from 'react-table';
import Card from '../../../components/Card/Card';
import {YCTable} from '../../../components/YCTable/YCTable';
import {useEventStatsConfig} from '../../../hooks/useEventStatsConfig';
import {PlayerStats, StatKey} from '../../../types';
import {OverrideScoreFieldInput} from './OverrideScoreFieldInput';
import { useSetMvpMutation } from 'app/api';

export interface TeamScoringPanelProps {
  gameId: number;
  mvpId?: number;
  overriding?: boolean;
  editable?: boolean;
  teamName: string;
  players: PlayerStats[];
  setSelectedPlayer: (player: PlayerStats) => void;
  triggerRefetch?: () => void;
}

const STAT_COLUMN_DEFS: { key: StatKey; header: string }[] = [
  {key: 'onePoints', header: 'FT'},
  {key: 'twoPoints', header: '2PT'},
  {key: 'threePoints', header: '3PT'},
  {key: 'assists', header: 'Assists'},
  {key: 'rebounds', header: 'Rebounds'},
  {key: 'steals', header: 'Steals'},
  {key: 'blocks', header: 'Blocks'},
  {key: 'turnovers', header: 'Turnovers'},
  {key: 'fouls', header: 'Fouls'},
  {key: 'foulsDrawn', header: 'Fouls Drawn'},
];

const OVERRIDE_HEADERS: Record<StatKey, string> = {
  onePoints: 'Free Throw',
  twoPoints: '2PT',
  threePoints: '3PT',
  assists: 'Assists',
  rebounds: 'Rebounds',
  steals: 'Steals',
  blocks: 'Blocks',
  turnovers: 'Turnovers',
  fouls: 'Fouls',
  foulsDrawn: 'Fouls Drawn',
};

export const TeamScoringPanel: React.FC<TeamScoringPanelProps> = ({
  gameId,
  mvpId,
  teamName,
  players,
  setSelectedPlayer,
  overriding,
  editable,
  triggerRefetch = () => {
                                                                    },
                                                                  }) => {
  const {isStatEnabled, anyPointsEnabled} = useEventStatsConfig();

  const totalPoints = players.reduce(
    (acc, player) =>
      acc + player.twoPoints * 2 + player.threePoints * 3 + player.onePoints,
    0
  );
  const totalAssists = players.reduce((acc, player) => acc + player.assists, 0);
  const totalRebounds = players.reduce(
    (acc, player) => acc + player.rebounds,
    0
  );
  const totalSteals = players.reduce((acc, player) => acc + player.steals, 0);
  const totalBlocks = players.reduce((acc, player) => acc + player.blocks, 0);
  const totalFouls = players.reduce((acc, player) => acc + player.fouls, 0);
  const totalTurnovers = players.reduce((acc, player) => acc + player.turnovers, 0);
  const totalFoulsDrawn = players.reduce((acc, player) => acc + player.foulsDrawn, 0);

  const columns = React.useMemo(
    () => {
      const cols: any[] = [
        {
          Header: 'Name',
          accessor: 'name',
          Cell: (cell: Cell<PlayerStats>) => (
            <Input
              w={{base: '100%', md: '90%'}}
              onClick={(e) => e.stopPropagation()}
              placeholder={cell.value}
            />
          ),
        },
      ];
      STAT_COLUMN_DEFS.forEach(({key, header}) => {
        if (isStatEnabled(key)) {
          cols.push({Header: header, accessor: key});
        }
      });
      cols.push({
        Header: 'MVP',
        accessor: 'mvp',
        Cell: (cell: Cell<PlayerStats>) => {
          if (mvpId === cell.row.original.playerId) {
            return <Tag colorScheme="green">MVP</Tag>;
          } else {
            return <SetMvpButton gameId={gameId} player={cell.row.original} triggerRefetch={triggerRefetch} />
          }
        },
      });
      return cols;
    },
    [gameId, mvpId, triggerRefetch, isStatEnabled]
  );

  const overridableColumns = React.useMemo(
    () => {
      const cols: any[] = [
        {
          Header: 'Name',
          accessor: 'name',
        },
      ];
      STAT_COLUMN_DEFS.forEach(({key}) => {
        if (isStatEnabled(key)) {
          cols.push({
            Header: OVERRIDE_HEADERS[key],
            accessor: key,
            Cell: (cell: Cell<PlayerStats>) => (
              <OverrideScoreFieldInput
                cell={cell}
                triggerRefetch={triggerRefetch}
              />
            ),
          });
        }
      });
      cols.push({
        Header: 'MVP',
        accessor: 'mvp',
        Cell: (cell: Cell<PlayerStats>) => (
          <SetMvpButton gameId={gameId} player={cell.row.original} triggerRefetch={triggerRefetch} />
        ),
      });
      return cols;
    },
    [gameId, triggerRefetch, isStatEnabled]
  );

  return (
    <Card>
      <Heading size="md">{teamName}</Heading>
      <StatGroup>
        {anyPointsEnabled && (
          <Stat>
            <StatLabel>Points</StatLabel>
            <StatNumber>{totalPoints}</StatNumber>
          </Stat>
        )}
        {isStatEnabled('assists') && (
          <Stat>
            <StatLabel>Assists</StatLabel>
            <StatNumber>{totalAssists}</StatNumber>
          </Stat>
        )}
        {isStatEnabled('rebounds') && (
          <Stat>
            <StatLabel>Rebounds</StatLabel>
            <StatNumber>{totalRebounds}</StatNumber>
          </Stat>
        )}
        {isStatEnabled('steals') && (
          <Stat>
            <StatLabel>Steals</StatLabel>
            <StatNumber>{totalSteals}</StatNumber>
          </Stat>
        )}
        {isStatEnabled('blocks') && (
          <Stat>
            <StatLabel>Blocks</StatLabel>
            <StatNumber>{totalBlocks}</StatNumber>
          </Stat>
        )}
        {isStatEnabled('turnovers') && (
          <Stat>
            <StatLabel>Turnovers</StatLabel>
            <StatNumber>{totalTurnovers}</StatNumber>
          </Stat>
        )}
        {isStatEnabled('fouls') && (
          <Stat>
            <StatLabel>Fouls</StatLabel>
            <StatNumber>{totalFouls}</StatNumber>
          </Stat>
        )}
        {isStatEnabled('foulsDrawn') && (
          <Stat>
            <StatLabel>Fouls Drawn</StatLabel>
            <StatNumber>{totalFoulsDrawn}</StatNumber>
          </Stat>
        )}
      </StatGroup>
      <YCTable
        onRowClick={({original}: Row<PlayerStats>) => {
          if (editable && !overriding) {
            setSelectedPlayer(original);
          }
        }}
        columns={overriding ? overridableColumns : columns}
        data={players}
      />
    </Card>
  );
};

const SetMvpButton: React.FC<{ gameId: number; player: PlayerStats; triggerRefetch: () => void }> = ({ gameId, player, triggerRefetch }) => {
  const [setMvp] = useSetMvpMutation();
  return <Button onClick={(e) => {
    e.stopPropagation();
    setMvp({ gameId, playerId: player.playerId });
    triggerRefetch();
  }}>Set MVP</Button>;
};
